


import { useRef, useState,useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'Assessment/src/components/ui/card';
import { Badge } from 'Assessment/src/components/ui/badge';
import { Button } from 'Assessment/src/components/ui/button';
import { Separator } from 'Assessment/src/components/ui/separator';
import { ArrowUp, ArrowDown, Minus, CheckCircle2, XCircle, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ScenarioCard } from './ScenarioCard';
import { assessmentGet } from 'utils/assessmentApplication/assessmentService';
import { useColor } from "components/ui/ColorContext";




interface EvaluationData {
  pre_assessment: {
    total: number;
    [key: string]: any;
  };
  post_assessment: {
    total: number;
    [key: string]: any;
  };
  comparison: {
    score_difference: number;
    improvement_level: string;
    summary: string;
    examples_of_change?: string[];
  };
  delta: number;
}

interface EvaluationResultsProps {
  evaluation: EvaluationData;
  assessmentId: string;
  assessmentDetails?: any;
  learnerName?: string;
  data?: any;
  contentByLanguage?: string | null;
}

const parseBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  return false;
};

export const EvaluationResults = ({ evaluation, assessmentId, assessmentDetails, learnerName, data, contentByLanguage }: EvaluationResultsProps) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);
  const [assessmentData, setAssessmentData] = useState(assessmentDetails);
  const contentRef = useRef<HTMLDivElement>(null);
const { primaryColor } = useColor(); 
  // Fetch assessment details if not provided
  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      if (!assessmentData && assessmentId) {
        try {
          const result = await assessmentGet(assessmentId);
          if (result?.updatedData) {
            setAssessmentData(result.updatedData);
          }
        } catch (error) {
          console.error('Failed to fetch assessment details:', error);
        }
      }
    };
    
    fetchAssessmentDetails();
  }, [assessmentId, assessmentData]);

  const waitForLayout = async () => {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  };

  const generatePDF = async () => {
    if (!contentRef.current || isGeneratingPDF) return;

    setIsGeneratingPDF(true);
    setPdfMode(true);

    // Add PDF mode attribute for compact styling
    contentRef.current.setAttribute('data-pdf', 'true');

    try {
      // Ensure the PDF-only DOM has rendered before html2canvas runs
      await waitForLayout();

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 5; // mm margin

      // Draw title directly with jsPDF (avoids html2canvas text clipping)
      const pdfTitle = 'Your Practice Results';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      const titleY = margin + 10;
      doc.text(pdfTitle, pageWidth / 2, titleY, { align: 'center' });

      // Start content below the title
      let currentY = titleY + 8;
      let isFirstSection = true;

      // Capture only the marked sections (title is excluded and drawn by jsPDF)
      const sections = contentRef.current.querySelectorAll('[data-pdf-section]');

      for (const section of sections) {
        const canvas = await html2canvas(section as HTMLElement, {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            // Avoid scrolling the real page (prevents jump-to-top). Only normalize scroll inside the cloned DOM.
            clonedDoc.documentElement.scrollTop = 0;
            clonedDoc.body.scrollTop = 0;
            // Force light mode for PDF capture - removes dark mode styling
            clonedDoc.documentElement.classList.remove('dark');
            clonedDoc.body.classList.remove('dark');
          },
        });

        const imgWidth = pageWidth - margin * 2;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if section fits on current page
        if (!isFirstSection && currentY + imgHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, currentY, imgWidth, imgHeight);

        currentY += imgHeight + 2; // 2mm gap between sections
        isFirstSection = false;
      }

      // Save with learner name
      const sanitizedName = learnerName
        ? learnerName.replace(/[^a-zA-Z0-9\s]/g, '').trim()
        : 'Learner';
      doc.save(`${sanitizedName} Practice Results.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      // Always restore - runs even if error occurs
      contentRef.current?.removeAttribute('data-pdf');
      setPdfMode(false);
      setIsGeneratingPDF(false);
    }
  };

  const getDeltaIcon = () => {
    if (evaluation.delta > 0) return <ArrowUp className="h-5 w-5" />;
    if (evaluation.delta < 0) return <ArrowDown className="h-5 w-5" />;
    return <Minus className="h-5 w-5" />;
  };

  const getDeltaColor = () => {
    if (evaluation.delta > 0) return 'text-green-600 dark:text-green-400';
    if (evaluation.delta < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getImprovementBadgeStyle = (level: string) => {
    if (level === 'Improved') return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700';
    if (level === 'Same') return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600';
    if (level === 'Declined') return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700';
    return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600';
  };

  const getImprovementTextColor = (level: string) => {
    if (level === 'Improved') return 'text-green-700 dark:text-green-400';
    if (level === 'Same') return 'text-gray-700 dark:text-gray-300';
    if (level === 'Declined') return 'text-orange-700 dark:text-orange-400';
    return 'text-gray-700 dark:text-gray-300';
  };

  //divakar
  const getMaxScore = (key: string, value: any): number => {
    // Priority 1: Use max_score from JSON if provided
    if (typeof value === 'object' && value?.max_score !== undefined) {
      return value.max_score;
    }
    // Priority 2: Fallback for legacy data - tone keys = 1, others = 3
    return key.toLowerCase().includes('tone') ? 1 : 3;
  };

  const preAttempts = Number(assessmentData?.assessmentPreAttempts ?? assessmentData?.pre_attempts ?? 0);
  const postAttempts = Number(assessmentData?.assessmentPostAttempts ?? assessmentData?.post_attempts ?? 0);
  const preValid = parseBoolean(assessmentData?.assessmentPreValid ?? assessmentData?.pre_valid);
  const postValid = parseBoolean(assessmentData?.assessmentPostValid ?? assessmentData?.post_valid);
  const preTranscript = assessmentData?.assessmentPreTranscript ?? assessmentData?.pre_transcript;
  const postTranscript = assessmentData?.assessmentPostTranscript ?? assessmentData?.post_transcript;
  const assessmentDelta = assessmentData?.assessmentDelta ?? assessmentData?.delta;
  const preTrainingScore = evaluation.pre_assessment?.total ?? (preValid ? 10 : 0);
  const postTrainingScore = evaluation.post_assessment?.total ?? (postValid ? 10 : 0);
  const scoreDelta =
    evaluation.delta !== undefined && evaluation.delta !== null
      ? evaluation.delta
      : assessmentDelta !== undefined && assessmentDelta !== null
        ? assessmentDelta
        : (postTrainingScore - preTrainingScore);

  return (
    <div className="flex justify-center">
      <style>
        {`
          [data-pdf="true"] * {
            color: black !important;
          }
          [data-pdf="true"] .scenario-card1 {
            width: 100% !important;
            max-width: 100% !important;
          }
        `}
      </style>
      <div ref={contentRef} className="w-full max-w-6xl mx-auto space-y-6 p-6 pdf-container">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white"
          style={{color:primaryColor}}
          >Result</h1>
        </div>
      <Card 
      style={{
          fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
      data-pdf-section>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-lg text-white-foreground mb-1">Pre-Training Attempts</p>
              <p className="text-2xl">{preAttempts}</p>
              <div className="flex items-center gap-2 mt-2">
              {preValid ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-lg text-green-600 dark:text-green-400">Response Valid</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-lg text-red-600 dark:text-red-400">Response Not Valid</span>
                </>
              )}
              </div>
            </div>
            <div>
              <p className="text-lg text-white-foreground mb-1">Post-Training Attempts</p>
              <p className="text-2xl">{postAttempts}</p>
              <div className="flex items-center gap-2 mt-2">
              {postValid ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-lg text-green-600 dark:text-green-400">Response Valid</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-lg text-red-600 dark:text-red-400">Response Not Valid</span>
                </>
              )}
              </div>
            </div>
          </div>
          
          <Separator className="my-6" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-lg text-white-foreground mb-2">Pre-Training Score</p>
              <p className="text-3xl">
                {preTrainingScore}
              </p>
            </div>
            <div className="text-center">
              <p className="text-lg text-white-foreground mb-2">Post-Training Score</p>
              <p className="text-3xl">
                {postTrainingScore}
              </p>
            </div>
            <div className="text-center">
              <p className="text-lg text-white-foreground mb-2">Score Change</p>
              <div className="flex items-center justify-center gap-2">
                {/* {getDeltaIcon()} */}
                <p className={"text-3xl"}>
                  {scoreDelta > 0 ? '+' : ''}{scoreDelta}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Card */}
      <div data-pdf-section className="scenario-card1">
        <ScenarioCard
            style={{
              fontSize: "16px",
              background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
              borderColor: `${primaryColor}aa`,
              boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
            }}
            data={data}
            type={"post"}
          />
      </div>

      {/* Pre and Post Transcripts Side by Side */}
      <div data-pdf-section className="flex flex-col md:flex-row gap-6">
        <Card 
        style={{
          fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
        className="flex-1 min-w-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Pre-Training Response
              {preValid ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preTranscript ? (
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-lg whitespace-pre-wrap text-foreground">{preTranscript}</p>
              </div>
            ) : (
              <p className="text-lg text-white-foreground">No transcript available</p>
            )}
          </CardContent>
        </Card>

        <Card 
        style={{
          fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
        className="flex-1 min-w-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Post-Training Response
              {postValid ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {postTranscript ? (
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-lg whitespace-pre-wrap text-foreground">{postTranscript}</p>
              </div>
            ) : (
              <p className="text-lg text-white-foreground">No transcript available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Comparison */}
      {evaluation.comparison && (
        <Card 
        style={{
          fontSize: "13px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
        data-pdf-section>
          <CardHeader>
            <CardTitle>Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <h3 style={{fontSize:'1.5rem'}}className="font-semibold">Improvement Level:</h3>
              {pdfMode ? (
                <span className={`font-semibold ${getImprovementTextColor(evaluation.comparison.improvement_level)}`}>
                  {evaluation.comparison.improvement_level}
                </span>
              ) : (
                <Badge
                  className={`text-base px-3 py-1 ${getImprovementBadgeStyle(
                    evaluation.comparison.improvement_level
                  )}`}
                >
                  {evaluation.comparison.improvement_level}
                </Badge>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2"
              style={{color:primaryColor,fontSize:'1.5rem'}}
              >Summary</h3>
              <p style={{fontSize:'1rem'}} className="text-white-foreground text-md">{evaluation.comparison.summary}</p>
            </div>
            
            {evaluation.comparison.examples_of_change && evaluation.comparison.examples_of_change.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3"
                style={{fontSize:'1.5rem',color:primaryColor}}
                >Key Changes</h3>
                <div className="space-y-3">
                  {evaluation.comparison.examples_of_change.map((example, index) => {
                    // Parse category from example
                    const categoryMatch = example.match(/^(WHAT|SO WHAT|NOW WHAT|TONE):/);
                    const category = categoryMatch ? categoryMatch[1] : null;
                    
                    // Determine color based on score change for this category
                    let colorClasses = 'border-l-gray-400 bg-gray-100/80 dark:bg-gray-800/50 dark:border-l-gray-500'; // default
                    
                    if (category && evaluation.pre_assessment && evaluation.post_assessment) {
                      const categoryKey = category === 'SO WHAT' ? 'so_what' 
                        : category === 'NOW WHAT' ? 'now_what'
                        : category === 'TONE' ? 'tone_empathy'
                        : category.toLowerCase();
                      
                      const preValue = evaluation.pre_assessment[categoryKey];
                      const postValue = evaluation.post_assessment[categoryKey];
                      const preScore = typeof preValue === 'object' ? preValue.score : preValue;
                      const postScore = typeof postValue === 'object' ? postValue.score : postValue;
                      
                      if (postScore > preScore) {
                        colorClasses = 'border-l-green-500 bg-green-100/80 dark:bg-green-900/30 dark:border-l-green-400';
                      } else if (postScore < preScore) {
                        colorClasses = 'border-l-amber-500 bg-amber-100/80 dark:bg-amber-900/30 dark:border-l-amber-400';
                      }
                    }
                    
                    return (
                      <div 
                       style={{
          fontSize: "1rem" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
                        key={index}
                        className={`border-l-4 ${colorClasses} p-3 rounded-r text-lg`}
                      >
                        {example}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detailed Score Breakdown */}
      <Card 
      style={{
          fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
      data-pdf-section>
        <CardHeader>
          <CardTitle>Score Breakdown by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th style={{fontSize:'1rem'}} className="text-left py-3 px-2 font-semibold text-md">Category</th>
                  <th style={{fontSize:'1rem'}} className="text-center py-3 px-4 font-semibold text-md">Pre-Training</th>
                  <th style={{fontSize:'1rem'}} className="text-center py-3 px-4 font-semibold text-md">Post-Training</th>
                </tr>
              </thead>
              <tbody>
                {evaluation.pre_assessment && evaluation.post_assessment && 
                  // Object.keys(evaluation.pre_assessment).map((key) => {
                  //   if (key === 'total' || key === 'comment') return null;
                  ((evaluation as any).category_order || Object.keys(evaluation.pre_assessment))
.filter((k: string) => k !== 'total' && k !== 'comment')
.map((key: string) => {
                    
                    const preValue = evaluation.pre_assessment[key];
                    const postValue = evaluation.post_assessment[key];
                    
                    // Extract scores and comments for both
                    const preScore = typeof preValue === 'object' && preValue.score !== undefined 
                      ? preValue.score 
                      : preValue;
                    const preComment = typeof preValue === 'object' && preValue.comment 
                      ? preValue.comment 
                      : null;
                    const postScore = typeof postValue === 'object' && postValue.score !== undefined 
                      ? postValue.score 
                      : postValue;
                    const postComment = typeof postValue === 'object' && postValue.comment 
                      ? postValue.comment 
                      : null;
                    
                    return (
                      <tr key={key} className="border-b border-border/50">
                        <td style={{fontSize:'1rem'}} className="py-4 px-2 font-medium capitalize align-top">
                          {key === 'tone_empathy' ? 'Tone' : key.replace(/_/g, ' ')}
                        </td>
                        <td className="py-4 px-4 align-top">
                          <div className="space-y-2">
                            <div className="text-center font-semibold text-md">
                              {/* {preScore}/{key === 'tone_empathy' ? '1' : '3'} */}
                              {preScore}/{getMaxScore(key, preValue)}
                            </div>
                            {preComment && (
                              <p className="text-lg text-white-foreground text-left">{preComment}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 align-top">
                          <div className="space-y-2">
                            <div className="text-center font-semibold text-md">
                              {/* {postScore}/{key === 'tone_empathy' ? '1' : '3'} */}
                              {postScore}/{getMaxScore(key, postValue)}
                            </div>
                            {postComment && (
                              <p className="text-lg text-white-foreground text-left">{postComment}</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
                <tr className="border-t-2 border-border">
                  <td style={{fontSize:'1rem'}} className="py-4 px-2 font-bold text-md">Total</td>
                  <td className="py-4 px-4 text-center text-xl font-bold">
                    {evaluation.pre_assessment?.total || 0}
                  </td>
                  <td className="py-4 px-4 text-center text-xl font-bold">
                    {evaluation.post_assessment?.total || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Footnote disclaimer */}
          <p className="text-lg text-white-foreground italic mt-4 pt-3 border-t">
            Note: This feedback is AI-generated. Please interpret the results with discretion.
          </p>
        </CardContent>
      </Card>
      
        <div className="pdf-exclude text-center space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4 text-lg">
          <Button 
          style={{
          // fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
            onClick={generatePDF}
            variant="default"
            size="lg"
            className="gap-2"
            disabled={isGeneratingPDF}
          >
            <Download 
            style={{
          // fontSize: "16px" ,
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
            className="h-4 w-4" />
            {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </div>
        </div>
    </div>
    </div>
  );
};
